import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { beaaHeroImage } from '@/mocks/beaa';
import { BEAA_OPEN_CHAT_EVENT } from '@/utils/beaaChat';
import { getSecaoAtivaManual, type SecaoManualAtiva } from '@/utils/beaaContextoSecao';
import { beaaPortalProfile } from '@/utils/beaaAssistant';
import { temNavInferiorAtiva } from '@/utils/navInferior';
import type { BeaaAssistantEntry, BeaaAssistantProfile } from '@/utils/beaaAssistant';
import BeaaAssistantPanel from './BeaaAssistantPanel';

type BeaaMascotScope = 'portal' | 'console';

interface BeaaMascotButtonProps {
  /** Perfil de conteúdo do chat (portal do cidadão ou Console Administrativo). */
  profile?: BeaaAssistantProfile;
  /**
   * portal: aparece em todo o site público (esconde-se no /admin).
   * console: aparece apenas no Console Administrativo.
   */
  scope?: BeaaMascotScope;
  /** Resolvedor assíncrono opcional de respostas (usado no Console com os manuais). */
  resolver?: (pergunta: string) => Promise<BeaaAssistantEntry>;
}

export default function BeaaMascotButton({
  profile = beaaPortalProfile,
  scope = 'portal',
  resolver,
}: BeaaMascotButtonProps) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [perguntaInicial, setPerguntaInicial] = useState<string | null>(null);
  const [contextoSecao, setContextoSecao] = useState<SecaoManualAtiva | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const isAdminArea = pathname.startsWith('/admin') || pathname.startsWith('/upavet');
  // Quando a tela atual tem a barra de navegação inferior do módulo (celular e
  // tablet), o mascote precisa subir para não cobrir os itens do menu. A marca
  // vem do próprio shell que desenha a barra, então vale para qualquer rota
  // nova que use esse shell — inclusive a Caderneta Digital.
  const [temNavInferior, setTemNavInferior] = useState(temNavInferiorAtiva);

  useEffect(() => {
    const sincronizar = () => setTemNavInferior(temNavInferiorAtiva());
    sincronizar();
    const observador = new MutationObserver(sincronizar);
    observador.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observador.disconnect();
  }, []);
  // Portal não aparece no Console; a versão do Console só aparece lá.
  // O PETBEA Campo (app de vistoria, também sem conexão) tem barra própria de botões grandes: sem mascote.
  const visible = (scope === 'console' ? isAdminArea : !isAdminArea) && !pathname.startsWith('/campo');

  // Marca a página como tendo o lançador da Beaa, escondendo o balão genérico do assistente
  useEffect(() => {
    if (!visible) return () => {};
    document.body.classList.add('beaa-chat-launcher');
    return () => document.body.classList.remove('beaa-chat-launcher');
  }, [visible]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKey);
    };
  }, []);

  // Abre o painel de conversa quando algum botão da página pede o chat da Beaa.
  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detalhe = (event as CustomEvent<{ mensagemInicial?: string }>).detail;
      setPerguntaInicial(detalhe?.mensagemInicial ?? null);
      setContextoSecao(null);
      setOpen(false);
      setChatOpen(true);
    };
    window.addEventListener(BEAA_OPEN_CHAT_EVENT, handleOpen);
    return () => window.removeEventListener(BEAA_OPEN_CHAT_EVENT, handleOpen);
  }, []);

  const handleOpenChat = () => {
    setPerguntaInicial(null);
    // Captura a seção do manual visível para a Beaa já se contextualizar.
    setContextoSecao(getSecaoAtivaManual());
    setOpen(false);
    setChatOpen(true);
  };

  const fecharChat = () => {
    setChatOpen(false);
    setPerguntaInicial(null);
    setContextoSecao(null);
  };

  if (!visible) {
    return null;
  }

  return (
    <>
    <div
      ref={wrapRef}
      className={`fixed right-4 z-50 transition-all duration-500 translate-y-0 opacity-100 md:right-5 ${
        temNavInferior
          ? 'bottom-[calc(6.5rem+env(safe-area-inset-bottom))] lg:bottom-5'
          : 'bottom-5'
      }`}
    >
      {/* Panel */}
      {open && (
        <div className="absolute bottom-[4.75rem] right-0 w-[19rem] max-w-[calc(100vw-2.5rem)] rounded-2xl border border-background-200/70 bg-background-50 shadow-warm overflow-hidden">
          {/* Header */}
          <div className="relative bg-primary-500 px-4 py-3.5 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-background-50 shrink-0">
              <img
                src={beaaHeroImage}
                alt="Beaa"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-heading font-bold text-background-50 leading-tight">
                {profile.menuTitle}
              </p>
              <p className="text-[11px] text-background-50/85 leading-tight">
                {profile.menuSubtitle}
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Fechar"
              className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-background-50/85 hover:text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>

          {/* Quote */}
          {profile.quote && (
            <div className="px-4 pt-3.5 pb-1">
              <p className="text-xs text-foreground-600 leading-relaxed flex items-start gap-1.5">
                <i className="ri-double-quotes-l text-primary-500 mt-0.5 shrink-0" />
                {profile.quote}
              </p>
            </div>
          )}

          {/* Chat com a Beaa */}
          <div className="px-2.5 pt-2.5">
            <button
              type="button"
              onClick={handleOpenChat}
              className="w-full flex items-center gap-3 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 px-2.5 py-2.5 transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 rounded-lg bg-background-50/15 flex items-center justify-center shrink-0">
                <i className="ri-chat-smile-3-line" />
              </span>
              <span className="min-w-0 text-left">
                <span className="block text-sm font-semibold leading-tight">{profile.menuChatTitle}</span>
                <span className="block text-[11px] text-background-50/85 leading-tight">
                  {profile.menuChatSubtitle}
                </span>
              </span>
              <i className="ri-arrow-right-line ml-auto" />
            </button>
          </div>

          {/* Quick actions */}
          {profile.quickActions.length > 0 && (
            <div className="p-2.5 space-y-1">
              {profile.quickActions.map((action) => (
                <Link
                  key={action.to}
                  to={action.to}
                  onClick={() => setOpen(false)}
                  className="group flex items-center gap-3 rounded-xl px-2.5 py-2 hover:bg-background-100 transition-colors"
                >
                  <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                    <i className={action.icon} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-950 leading-tight">
                      {action.label}
                    </span>
                    <span className="block text-[11px] text-foreground-500 leading-tight">
                      {action.description}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line ml-auto text-foreground-300 group-hover:text-primary-500 transition-colors" />
                </Link>
              ))}
            </div>
          )}

          {/* WhatsApp */}
          {profile.whatsapp && (
            <div className="px-2.5 pb-2.5">
              <a
                href={profile.whatsapp.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 transition-colors rounded-xl px-4 py-2.5"
              >
                <i className="ri-whatsapp-line" />
                {profile.whatsapp.label}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Mascot button */}
      <button
        onClick={() => {
          if (chatOpen) {
            setChatOpen(false);
            return;
          }
          setOpen((prev) => !prev);
        }}
        aria-label={open || chatOpen ? 'Fechar ajuda da Beaa' : 'Abrir ajuda da Beaa'}
        aria-expanded={open || chatOpen}
        className="relative w-16 h-16 md:w-[4.5rem] md:h-[4.5rem] rounded-full bg-primary-500 flex items-center justify-center border-2 border-background-50 transition-transform duration-300 hover:scale-105 active:scale-95 cursor-pointer"
      >
        <span
          className={`w-full h-full rounded-full overflow-hidden ${
            open || chatOpen ? '' : 'beaa-bounce'
          }`}
        >
          <img
            src={beaaHeroImage}
            alt="Beaa, mascote do bem estar animal"
            className="w-full h-full object-cover object-top"
          />
        </span>
        {!open && !chatOpen && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4">
            <span className="whatsapp-ping absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-4 w-4 bg-accent-500" />
          </span>
        )}
      </button>
    </div>

    {/* Painel de conversa próprio da Beaa (substitui o widget genérico) */}
    {chatOpen && (
      <BeaaAssistantPanel
        profile={profile}
        resolver={resolver}
        perguntaInicial={perguntaInicial}
        contextoSecao={contextoSecao}
        onClose={fecharChat}
      />
    )}
    </>
  );
}